import { useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { useAdminManga, useAdminVolumes, useAdminChapters, useCreateVolume, useCreateChapter, useUploadChapter } from '../api/useAdmin'
import NotFound from './NotFound'
import { getErrMsg } from '../api/client'
import toast from 'react-hot-toast'

const STEPS = ['volume', 'chapter', 'upload']
const STEP_LABELS = ['Том', 'Глава', 'ZIP']

function StepBar({ step }) {
  const current = STEPS.indexOf(step)
  return (
    <div className="flex items-center gap-0 mb-8">
      {STEP_LABELS.map((label, i) => {
        const done = i < current
        const active = i === current
        return (
          <div key={label} className="flex items-center">
            <div className="flex items-center gap-2">
              <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold shrink-0
                ${done || active ? 'bg-accent text-on-accent' : 'bg-surface2 text-muted'}`}>
                {done ? '✓' : '●'}
              </div>
              <span className={`text-sm font-medium ${active ? 'text-fg' : done ? 'text-accent' : 'text-muted'}`}>
                {label}
              </span>
            </div>
            {i < STEP_LABELS.length - 1 && (
              <div className={`w-10 h-px mx-3 shrink-0 ${i < current ? 'bg-accent' : 'bg-line'}`} />
            )}
          </div>
        )
      })}
    </div>
  )
}

export default function AdminChapterUpload() {
  const { id: mangaId } = useParams()
  const { data: manga, isError } = useAdminManga(mangaId)
  const { data: rawVolumes = [], refetch: refetchVolumes } = useAdminVolumes(mangaId)
  const { data: allChapters = [] } = useAdminChapters(mangaId)
  const usedNumbers = allChapters.map((c) => c.number)
  const nextFree = usedNumbers.length > 0 ? Math.max(...usedNumbers) + 1 : 1

  const formatRanges = (nums) => {
    if (!nums.length) return ''
    const sorted = [...nums].sort((a, b) => a - b)
    const ranges = []
    let start = sorted[0], end = sorted[0]
    for (let i = 1; i < sorted.length; i++) {
      if (sorted[i] === end + 1) end = sorted[i]
      else { ranges.push(start === end ? `${start}` : `${start}–${end}`); start = end = sorted[i] }
    }
    ranges.push(start === end ? `${start}` : `${start}–${end}`)
    return ranges.join(', ')
  }

  const { mutate: createVolume, isPending: creatingVol } = useCreateVolume(mangaId)
  const { mutate: createChapter, isPending: creatingCh } = useCreateChapter()
  const { mutate: doUpload, isPending: isUploading } = useUploadChapter()

  const [step, setStep] = useState('volume')
  const [selectedVolId, setSelectedVolId] = useState('')
  const [showNewVol, setShowNewVol] = useState(false)
  const [newVolNum, setNewVolNum] = useState('')
  const [chapterNum, setChapterNum] = useState('')
  const [chapterTitle, setChapterTitle] = useState('')
  const [createdChapterId, setCreatedChapterId] = useState(null)
  const [zipFile, setZipFile] = useState(null)
  const [progress, setProgress] = useState(0)
  const [uploadedPagesCount, setUploadedPagesCount] = useState(0)

  if (isError) return <NotFound />

  const selectedVol = rawVolumes.find((v) => v.id === selectedVolId)
  const chapterNumInt = parseInt(chapterNum)
  const chapterNumTaken = chapterNum !== '' && usedNumbers.includes(chapterNumInt)
  const chapterNumValid = chapterNum !== '' && !isNaN(chapterNumInt) && !chapterNumTaken

  const handleVolumeStep = () => {
    if (showNewVol) {
      if (!newVolNum) { toast.error('Укажите номер тома'); return }
      createVolume({ number: parseInt(newVolNum) }, {
        onSuccess: async (vol) => {
          await refetchVolumes()
          setSelectedVolId(vol.id)
          setShowNewVol(false)
          setStep('chapter')
        },
        onError: () => toast.error('Ошибка создания тома'),
      })
    } else {
      if (!selectedVolId) { toast.error('Выберите том'); return }
      setStep('chapter')
    }
  }

  const handleChapterStep = () => {
    if (!chapterNum) { toast.error('Укажите номер главы'); return }
    if (chapterNumTaken) { toast.error(`Глава ${chapterNum} уже существует`); return }
    createChapter({ volumeId: selectedVolId, number: chapterNumInt, title: chapterTitle || null }, {
      onSuccess: (ch) => { setCreatedChapterId(ch.id); setStep('upload') },
      onError: (err) => toast.error(getErrMsg(err, 'Ошибка')),
    })
  }

  const handleUpload = () => {
    if (!zipFile) { toast.error('Выберите ZIP-файл'); return }
    const fd = new FormData()
    fd.append('file', zipFile)
    doUpload({ chapterId: createdChapterId, formData: fd, onProgress: setProgress }, {
      onSuccess: (data) => { setUploadedPagesCount(data?.pages_count || 0); setStep('done'); toast.success('Глава загружена!') },
      onError: (err) => toast.error(getErrMsg(err, 'Ошибка загрузки')),
    })
  }

  const handleReset = () => {
    setStep('volume'); setSelectedVolId(''); setShowNewVol(false); setNewVolNum('')
    setChapterNum(''); setChapterTitle(''); setCreatedChapterId(null)
    setZipFile(null); setProgress(0); setUploadedPagesCount(0)
  }

  const inputCls = 'px-3 py-2.5 rounded-lg text-sm outline-none w-full bg-surface2 text-fg border border-line focus:border-accent transition-colors'

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 py-10">
      <div className="flex items-center gap-2 mb-2 flex-wrap text-sm">
        <Link to="/admin" className="text-muted hover:text-accent transition-colors">← Админ</Link>
        <span className="text-line">/</span>
        <Link to={`/admin/manga/${mangaId}`} className="text-muted hover:text-accent transition-colors">
          {manga?.title || 'Манга'}
        </Link>
        <span className="text-line">/</span>
        <span className="text-fg">Загрузить главу</span>
      </div>

      <div className="flex items-center justify-between mb-8">
        <h1 className="text-xl font-bold">
          Загрузить главу
          {manga?.title && <span className="ml-2 text-base font-normal text-muted">— {manga.title}</span>}
        </h1>
        {step !== 'done' && (
          <Link to={`/admin/manga/${mangaId}`} className="text-sm text-muted hover:text-red-400 transition-colors">
            Отмена
          </Link>
        )}
      </div>

      <StepBar step={step} />

      <div className="rounded-2xl p-6 flex flex-col gap-5 bg-surface border border-line">

        {step === 'volume' && (
          <>
            <div>
              <h2 className="font-semibold mb-1">В какой том загружаем?</h2>
              <p className="text-xs text-muted">Том — это группировка глав. Выберите существующий или создайте новый.</p>
            </div>

            {rawVolumes.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {rawVolumes.map((v) => {
                  const active = selectedVolId === v.id && !showNewVol
                  return (
                    <button key={v.id} onClick={() => { setSelectedVolId(v.id); setShowNewVol(false) }}
                      className={`px-4 py-2 rounded-lg text-sm font-medium transition-all border
                        ${active ? 'bg-accent text-on-accent border-accent' : 'bg-surface2 text-fg border-line hover:border-accent'}`}>
                      Том {v.number}{v.title ? ` — ${v.title}` : ''}
                      <span className="ml-1.5 text-xs opacity-60">
                        {v.chapters_count > 0 ? `${v.chapters_count} гл. внутри` : 'пустой'}
                      </span>
                    </button>
                  )
                })}
              </div>
            )}

            {!showNewVol ? (
              <button onClick={() => { setShowNewVol(true); setSelectedVolId('') }}
                className="self-start px-4 py-2 rounded-lg text-sm font-medium border border-dashed border-line text-accent hover:border-accent transition-colors">
                + Создать новый том
              </button>
            ) : (
              <div className="flex flex-col gap-3 p-4 rounded-xl border border-accent bg-accent-dim">
                <p className="text-sm font-medium text-accent">Новый том</p>
                <div className="flex items-center gap-3">
                  <input type="number" value={newVolNum} onChange={(e) => setNewVolNum(e.target.value)}
                    placeholder="Номер тома" className={inputCls} style={{ maxWidth: 160 }} autoFocus />
                  <button onClick={() => { setShowNewVol(false); setNewVolNum('') }}
                    className="text-sm text-muted hover:text-fg transition-colors">Отмена</button>
                </div>
              </div>
            )}

            <div className="flex gap-3 pt-1">
              <button onClick={handleVolumeStep} disabled={creatingVol || (!selectedVolId && !showNewVol)}
                className="px-5 py-2.5 rounded-xl text-sm font-bold transition-all hover:opacity-90 disabled:opacity-40 bg-accent text-on-accent">
                {creatingVol ? 'Создаём...' : 'Далее →'}
              </button>
            </div>
          </>
        )}

        {step === 'chapter' && (
          <>
            <div>
              <h2 className="font-semibold mb-1">Данные главы</h2>
              {selectedVol && (
                <p className="text-xs text-muted">Том {selectedVol.number}{selectedVol.title ? ` — ${selectedVol.title}` : ''}</p>
              )}
            </div>

            <div className="flex flex-col gap-1">
              <label className="text-xs font-medium text-muted">Номер главы *</label>
              <input type="number" value={chapterNum} onChange={(e) => setChapterNum(e.target.value)}
                placeholder={String(nextFree)} autoFocus style={{ maxWidth: 160 }}
                className={`px-3 py-2.5 rounded-lg text-sm outline-none w-full bg-surface2 text-fg transition-colors border ${
                  chapterNumTaken ? 'border-red-500' : chapterNumValid ? 'border-green-500' : 'border-line'
                }`}
              />
              <p className={`text-xs mt-0.5 ${chapterNumTaken ? 'text-red-400' : chapterNumValid ? 'text-green-400' : 'text-muted'}`}>
                {chapterNumTaken
                  ? `Глава ${chapterNum} уже существует`
                  : chapterNumValid ? 'Номер свободен'
                  : `Рекомендуем: ${nextFree}${usedNumbers.length > 0 ? ` · Занятые: ${formatRanges(usedNumbers)}` : ''}`}
              </p>
            </div>

            <div className="flex flex-col gap-1">
              <label className="text-xs font-medium text-muted">Название главы (необязательно)</label>
              <input type="text" value={chapterTitle} onChange={(e) => setChapterTitle(e.target.value)}
                placeholder="Например: Встреча в лесу" className={inputCls} />
            </div>

            <div className="flex gap-3 pt-1">
              <button onClick={handleChapterStep} disabled={creatingCh || !chapterNum || chapterNumTaken}
                className="px-5 py-2.5 rounded-xl text-sm font-bold transition-all hover:opacity-90 disabled:opacity-40 bg-accent text-on-accent">
                {creatingCh ? 'Создаём...' : 'Далее →'}
              </button>
              <button onClick={() => setStep('volume')}
                className="px-5 py-2.5 rounded-xl text-sm font-semibold border border-line text-muted hover:text-fg hover:border-accent transition-all">
                ← Назад
              </button>
            </div>
          </>
        )}

        {step === 'upload' && (
          <>
            <div>
              <h2 className="font-semibold mb-1">Загрузить страницы</h2>
              <p className="text-xs text-muted">ZIP-архив со страницами. Файлы будут отсортированы по имени (001.jpg, 002.jpg...).</p>
            </div>

            <label className="flex flex-col items-center justify-center gap-2 p-8 rounded-xl cursor-pointer transition-colors border-2 border-dashed border-line text-muted hover:border-accent">
              <span className="text-3xl">📦</span>
              <span className="text-sm font-medium">{zipFile ? zipFile.name : 'Нажмите, чтобы выбрать ZIP'}</span>
              {zipFile && <span className="text-xs">{(zipFile.size / 1024 / 1024).toFixed(1)} МБ</span>}
              <input type="file" accept=".zip" className="hidden" onChange={(e) => setZipFile(e.target.files[0])} />
            </label>

            {isUploading && (
              <div>
                <div className="flex justify-between text-xs mb-1.5 text-muted">
                  <span>Загрузка...</span><span>{progress}%</span>
                </div>
                <div className="h-1.5 rounded-full overflow-hidden bg-surface2">
                  <div className="h-full rounded-full bg-accent transition-all" style={{ width: `${progress}%` }} />
                </div>
              </div>
            )}

            <div className="flex gap-3 pt-1">
              <button onClick={handleUpload} disabled={!zipFile || isUploading}
                className="px-5 py-2.5 rounded-xl text-sm font-bold transition-all hover:opacity-90 disabled:opacity-40 bg-accent text-on-accent">
                {isUploading ? `Загрузка ${progress}%` : 'Загрузить'}
              </button>
              <button onClick={() => setStep('chapter')} disabled={isUploading}
                className="px-5 py-2.5 rounded-xl text-sm font-semibold border border-line text-muted hover:text-fg hover:border-accent transition-all disabled:opacity-40">
                ← Назад
              </button>
            </div>
          </>
        )}

        {step === 'done' && (
          <div className="flex flex-col items-center gap-4 py-4 text-center">
            <div className="text-4xl">✅</div>
            <div>
              <p className="font-semibold">Глава загружена!</p>
              {uploadedPagesCount > 0 && <p className="text-sm mt-1 text-muted">{uploadedPagesCount} страниц</p>}
            </div>
            <div className="flex gap-3">
              <button onClick={handleReset}
                className="px-5 py-2.5 rounded-xl text-sm font-bold transition-all hover:opacity-90 bg-accent text-on-accent">
                Загрузить ещё
              </button>
              <Link to={`/admin/manga/${mangaId}`}
                className="px-5 py-2.5 rounded-xl text-sm font-semibold border border-line text-muted hover:text-fg hover:border-accent transition-all">
                ← К манге
              </Link>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
